/**
 * A package's Source tab (RFC 0279 3d, S10): the files a fork would write, as
 * a tree, and the picked one read-only, the way a file in a repository opens.
 *
 * The bundle is fetched when this mounts, which is when the tab opens: never
 * to show the package page, never to install. `registry.source` checks the bytes
 * against the id that names them, so a mismatch arrives here as an error and
 * is said, not drawn as an empty tree.
 *
 * A file goes to whichever installed app handles its mime, falling back to the
 * one for `text/plain`, mounted on a read-only snapshot: the same dispatch a
 * file read at a commit takes (CodeFileView), so the colour is the Code app's
 * and a `.md` reads in the editor a markdown document does. No app installed
 * means a plain `<pre>`.
 */

import {
  type AppSummary,
  mimeFromPath,
  type SourceBundle,
} from '@mod/core/model'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { App, snapshotHandle } from '../../sdk/host/App'
import { useContribution } from '../../sdk/host/useContribution'
import { useWorldHandlers } from '../../services/world/useWorldHandlers'
import { ContributionWaiting } from '../../shell/slots/ContributionWaiting'
import { handlerFor } from '../../shell/slots/renderers'
import { buildSourceTree, firstSourceFile, type SourceNode } from './sourceTree'

type Loaded =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; bundle: SourceBundle }

export function PackageSource({
  sourceId,
  load,
  installedApps,
}: {
  sourceId: string
  /** The registry's hash-checked read, `registry.source`. */
  load: (sourceId: string) => Promise<SourceBundle>
  installedApps: AppSummary[]
}) {
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' })
  const [picked, setPicked] = useState<string | null>(null)
  const [closed, setClosed] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    let live = true
    setLoaded({ status: 'loading' })
    setPicked(null)
    load(sourceId).then(
      (bundle) => {
        if (!live) return
        setLoaded({ status: 'ready', bundle })
        setPicked(firstSourceFile(bundle.files.map((f) => f.path)))
      },
      (cause: unknown) => {
        if (live)
          setLoaded({
            status: 'error',
            message: cause instanceof Error ? cause.message : String(cause),
          })
      },
    )
    return () => {
      live = false
    }
  }, [sourceId, load])

  const files = loaded.status === 'ready' ? loaded.bundle.files : []
  const tree = useMemo(() => buildSourceTree(files.map((f) => f.path)), [files])
  const file = files.find((f) => f.path === picked) ?? null

  if (loaded.status === 'loading')
    return <p className="text-muted-foreground text-sm">Fetching source…</p>
  if (loaded.status === 'error')
    return (
      <div role="alert" className="text-red-500 text-sm">
        <p>Could not read this package's source.</p>
        <p className="mt-1 text-xs">{loaded.message}</p>
      </div>
    )
  if (files.length === 0)
    return (
      <p className="text-muted-foreground text-sm">
        The source was published with no files.
      </p>
    )

  const toggle = (path: string) =>
    setClosed((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })

  return (
    <div className="grid h-[70vh] min-h-[420px] grid-cols-[200px_minmax(0,1fr)] overflow-hidden rounded-xl border border-border">
      <nav
        aria-label="Source files"
        className="min-h-0 overflow-y-auto border-border border-r py-2 text-xs"
      >
        <Tree
          nodes={tree}
          depth={0}
          picked={picked}
          closed={closed}
          onPick={setPicked}
          onToggle={toggle}
        />
      </nav>
      {file ? (
        <SourceFile
          // Remount per file: a mounted app reads its snapshot once.
          key={file.path}
          id={`registry-source:${sourceId}:${file.path}`}
          path={file.path}
          content={file.content}
          installedApps={installedApps}
        />
      ) : (
        <p className="p-6 text-muted-foreground text-sm">Pick a file.</p>
      )}
    </div>
  )
}

function Tree({
  nodes,
  depth,
  picked,
  closed,
  onPick,
  onToggle,
}: {
  nodes: SourceNode[]
  depth: number
  picked: string | null
  closed: Set<string>
  onPick: (path: string) => void
  onToggle: (path: string) => void
}) {
  return (
    <ul>
      {nodes.map((node) => {
        const indent = { paddingLeft: 8 + depth * 12 }
        if (node.kind === 'folder') {
          const open = !closed.has(node.path)
          return (
            <li key={node.path}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => onToggle(node.path)}
                style={indent}
                className="flex w-full items-center gap-1 py-1 pr-2 text-left text-muted-foreground hover:text-foreground"
              >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {node.name}
              </button>
              {open && (
                <Tree
                  nodes={node.children}
                  depth={depth + 1}
                  picked={picked}
                  closed={closed}
                  onPick={onPick}
                  onToggle={onToggle}
                />
              )}
            </li>
          )
        }
        return (
          <li key={node.path}>
            <button
              type="button"
              aria-current={picked === node.path ? 'true' : undefined}
              onClick={() => onPick(node.path)}
              style={{ paddingLeft: indent.paddingLeft + 16 }}
              className={`block w-full truncate py-1 pr-2 text-left ${picked === node.path ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {node.name}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * One file: its path and line count over the app that handles its type, the
 * three steps a document takes. Ask who handles the mime, fetch that app's
 * code, mount it in the host realm on a snapshot that refuses writes.
 */
function SourceFile({
  id,
  path,
  content,
  installedApps,
}: {
  id: string
  path: string
  content: string
  installedApps: AppSummary[]
}) {
  const mime = mimeFromPath(path)
  const worldHandlers = useWorldHandlers()
  const app = useMemo(
    () =>
      handlerFor(installedApps, mime, worldHandlers) ||
      handlerFor(installedApps, 'text/plain', worldHandlers),
    [installedApps, mime, worldHandlers],
  )
  const view = useContribution(
    app?.realm === 'host' ? app.viewDocId : undefined,
  )
  const snapshot = useMemo(
    () => snapshotHandle(id, { path, mime, content }),
    [id, path, mime, content],
  )
  const lines = content === '' ? 0 : content.split('\n').length
  const parts = path.split('/')

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-3 border-border border-b px-4 py-2 text-xs">
        <span title={path} className="min-w-0 truncate">
          {parts.map((part, i) => (
            <span
              // Segments of one path: position is their identity.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional
              key={i}
              className={
                i === parts.length - 1
                  ? 'text-foreground'
                  : 'text-muted-foreground'
              }
            >
              {i > 0 && <span className="px-1 text-muted-foreground">/</span>}
              {part}
            </span>
          ))}
        </span>
        <span className="shrink-0 text-muted-foreground">
          {lines} {lines === 1 ? 'line' : 'lines'} · read-only
        </span>
      </div>
      {app?.realm === 'host' && view.state === 'loading' ? (
        <ContributionWaiting
          name={app.name}
          what="the code view's"
          recovery="Settings (⌘,) then Open with brings the plain view back."
        />
      ) : app?.realm === 'host' && view.state === 'ready' ? (
        <div className="relative flex min-h-0 flex-1 flex-col">
          <App
            key={`${app.id}:${view.code.length}`}
            code={view.code}
            documentId={id}
            // No project: a registry file is in none, so it takes no comments.
            projectId=""
            title={app.name}
            snapshot={snapshot}
          />
        </div>
      ) : (
        <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 font-mono text-foreground text-xs">
          {content}
        </pre>
      )}
    </div>
  )
}
